import styled from 'styled-components'

import { breakpoints, media, mediaMax } from 'styles/media'

export type GridColumns = Partial<Record<keyof typeof breakpoints, number>>

/* Four cards plus token gaps inside the app's standard 1300px content
   width — the densest default rendering. From xwide the floor relaxes to
   the app's long-standing 264px card minimum so the map panel keeps four
   across at 1920. */
export const GRID_MIN_CARD_WIDTH = 280
export const GRID_MIN_CARD_WIDTH_XWIDE = 264

export const defaultColumnsPerBreakpoint: GridColumns = {
  md: 2,
  lg: 3,
  xxxl: 4,
}

const breakpointOrder = Object.keys(breakpoints) as Array<
  keyof typeof breakpoints
>

type GridProps = {
  columns?: GridColumns
}

export const GridItem = styled.div.attrs({
  className: 'qa-grid-item',
})`
  min-width: 0;
`

/*
  Without `columns`, tracks auto-fill from the container width. An explicit
  `columns` ladder merges over the defaults, so partial overrides keep the
  remaining breakpoints. Full-width children (headings, see-more buttons)
  must opt out of the tracks with grid-column: 1 / -1.
*/

export const Grid = styled.div<GridProps>`
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(${GRID_MIN_CARD_WIDTH}px, 1fr)
  );
  column-gap: var(--ds-space-05);

  ${mediaMax.md`
    padding: 0 var(--ds-space-05);
  `}

  ${({ columns }) => {
    if (!columns) {
      return media.xwide`
        grid-template-columns: repeat(
          auto-fill,
          minmax(${GRID_MIN_CARD_WIDTH_XWIDE}px, 1fr)
        );
      `
    }

    const merged = { ...defaultColumnsPerBreakpoint, ...columns }

    return [
      'grid-template-columns: minmax(0, 1fr);',
      ...breakpointOrder
        .filter((name) => merged[name])
        .map(
          (name) => media[name]`
            grid-template-columns: repeat(${merged[name]}, minmax(0, 1fr));
          `,
        ),
    ]
  }}
`

/*
  A full-width strip that shows exactly one row of skeletons: it mirrors the
  auto-fill template at the same width, so its first row holds the parent
  grid's column count, and the zero-height implicit rows clip the overshoot.
  Only valid inside auto-fill grids — a `columns` ladder won't match it.
*/

export const GridSkeletonRow = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(${GRID_MIN_CARD_WIDTH}px, 1fr)
  );
  grid-template-rows: auto;
  grid-auto-rows: 0;
  column-gap: var(--ds-space-05);
  row-gap: 0;
  overflow: hidden;

  ${media.xwide`
    grid-template-columns: repeat(
      auto-fill,
      minmax(${GRID_MIN_CARD_WIDTH_XWIDE}px, 1fr)
    );
  `}
`
